import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type Calendar_Ical_SchoolCalendarConfigure,
  type QueryAdditionalOptions,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { toast } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { calendarKeys } from './keys';

const embeddableCalendarConfig = graphql(/* GraphQL */ `
  query calendar_ical_schoolCalendar {
    calendar_ical_schoolCalendar {
      enabled
      externalCalendarId
      externalCalendarLink
    }
  }
`);

const configureEmbeddableCalendar = graphql(/* GraphQL */ `
  mutation calendar_ical_schoolCalendarConfigure($input: Calendar_Ical_SchoolCalendarConfigure!) {
    calendar_ical_schoolCalendarConfigure(input: $input) {
      success
    }
  }
`);

const embeddableCalendarConfigQuery = {
  queryKey: calendarKeys.embeddableCalendarConfig(),
  queryFn: () => gqlClient.request(embeddableCalendarConfig),
};

export function useEmbeddableCalendarConfig(
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...embeddableCalendarConfigQuery,
    select: ({ calendar_ical_schoolCalendar }) => calendar_ical_schoolCalendar,
    ...additionalOptions,
  });
}

export function useUpdateEmbeddableCalendarConfig() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: Calendar_Ical_SchoolCalendarConfigure) =>
      gqlClient.request(configureEmbeddableCalendar, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.embeddableCalendarConfig(),
      });
      toast.success(t('common:snackbarMessages.updateSuccess'));
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export type ReturnTypeFromUseEmbeddableCalendarConfig = UseQueryReturnType<
  typeof useEmbeddableCalendarConfig
>;
